import React, { useState } from 'react';
import { 
  Play, AlertTriangle, Clock, FileVideo, ExternalLink
} from 'lucide-react';

const BodyCamPlayer = ({ videoId, title = "Body Camera Footage", caseInfo = "" }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  // Key violation timestamps - for reference display
  const violations = [
    { time: "0:00", label: "Arrival - No authorization obtained after stand down" },
    { time: "0:19", label: "Interaction with maintenance worker begins" },
    { time: "0:28", label: "Gallegos leads Hill to building - No consent from tenant" },
    { time: "0:45", label: "Entry with weapon drawn - Force continuum violated" },
    { time: "0:58", label: "Inside apartment - Child present" },
  ];

  return (
    <div className="bg-black rounded-lg overflow-hidden shadow-2xl">
      {/* Header */}
      <div className="bg-red-900 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileVideo className="w-5 h-5" />
          <div>
            <h3 className="font-bold text-sm">{title}</h3>
            <p className="text-red-200 text-xs">{caseInfo}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-red-200 text-xs">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
          EVIDENCE
        </div>
      </div>

      {/* YouTube Embed Container - Rotated 90° clockwise */}
      <div className="relative bg-black" style={{ paddingBottom: '56.25%' }}>
        {!isPlaying ? (
          <div 
            className="absolute inset-0 flex items-center justify-center cursor-pointer bg-gradient-to-b from-black/20 to-black/60"
            onClick={() => setIsPlaying(true)}
          >
            <div className="text-center">
              <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4 hover:bg-red-700 transition-colors shadow-lg">
                <Play className="w-10 h-10 text-white ml-1" />
              </div>
              <p className="text-white font-medium">Click to Play Body Cam Footage</p>
              <p className="text-gray-400 text-sm mt-1">State v. Ken Hill | February 21, 2022</p>
            </div>
          </div>
        ) : (
          <div 
            className="absolute inset-0 overflow-hidden"
          >
            {/* YouTube iframe - privacy enhanced, no related videos */}
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&showinfo=0&controls=1&disablekb=0&fs=1&iv_load_policy=3&end=0`}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0"
            />
          </div>
        )}
      </div>

      {/* Warning Banner */}
      <div className="bg-yellow-900/50 px-4 py-2 flex items-center gap-2 text-yellow-200 text-sm">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span>This footage contains the actual violations discussed in the case study questions below.</span>
      </div>

      {/* Violation Timeline */}
      <div className="bg-gray-800 px-4 py-3 border-t border-gray-700">
        <h4 className="text-white text-xs font-semibold mb-2 flex items-center gap-2">
          <Clock className="w-3 h-3" />
          KEY MOMENTS TO WATCH FOR
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {violations.map((v, idx) => (
            <div
              key={idx}
              className="text-xs px-3 py-2 rounded bg-gray-700 text-gray-300 flex items-start gap-2"
            >
              <span className="text-red-400 font-mono font-bold">{v.time}</span>
              <span>{v.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* External Link */}
      <div className="bg-gray-900 px-4 py-2 text-center">
        <a 
          href={`https://www.youtube.com/watch?v=${videoId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-gray-500 hover:text-gray-300 inline-flex items-center gap-1"
        >
          <ExternalLink className="w-3 h-3" />
          Open in YouTube
        </a>
      </div>
    </div>
  );
};

export default BodyCamPlayer;
